import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  open: boolean;
  /** Dismiss the prompt and leave the form open. */
  onKeepEditing: () => void;
  /** Confirm discard — the caller closes the form. */
  onDiscard: () => void;
  /** Override the default copy when a form has domain-specific wording. */
  title?: string;
  description?: string;
  /** Offer to SAVE on the way out as well — a third button, primary, last.
   *  The caller does the save and moves on (or stays, on failure). Omitted,
   *  the prompt is the usual keep-or-discard pair. */
  onSave?: () => void;
  saveLabel?: string;
  /** A save is in flight: every button waits on it. */
  saving?: boolean;
}

/**
 * Confirmation shown when a user tries to close a form (sheet/sidebar) that has
 * unsaved edits. Rendered on top of the form's own overlay; "Keep editing"
 * dismisses it, "Discard" tells the caller to close. Wire it up via
 * `useUnsavedChangesGuard`, which owns the open state and the close decision.
 */
export function DiscardChangesDialog({
  open,
  onKeepEditing,
  onDiscard,
  title = 'Discard unsaved changes?',
  description = "Your changes haven't been saved and will be lost. This can't be undone.",
  onSave,
  saveLabel = 'Save',
  saving = false,
}: Props): React.ReactElement {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // ESC / overlay click on the prompt itself means "keep editing" — the
        // safe choice. Only an explicit Discard click closes the form.
        if (!next && !saving) onKeepEditing();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onKeepEditing}>
            Keep editing
          </Button>
          <Button variant="destructive" disabled={saving} onClick={onDiscard}>
            Discard
          </Button>
          {onSave && (
            <Button disabled={saving} onClick={onSave}>
              {saving ? 'Saving…' : saveLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
